import React, { useState, useEffect } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import aboutBanner from '../../../assets/images/about-banner.jpg';
import oldBuilding from '../../../assets/images/old-building.jpg';
import rabbiFinkPortrait from '../../../assets/images/rabbi-dov-fink-portrait.jpeg';
import rabbiFinkFamily from '../../../assets/images/rabbi-dov-fink-family.jpeg';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Ratio from 'react-bootstrap/Ratio';
import { useParams } from 'react-router-dom';
import('./style.scss');

const About = () => {
  const { currentTab } = useParams();
  const [activeTabIndex, setActiveTabIndex] = useState();

  const tabs = ['', 'history', 'rabbi', 'membership', 'board', 'contact'];

  const boardColumns = [
    { position: 'President', names: ['Shmuli Shulman'] },
    { position: 'Immediate Past President', names: ['Max Oppenheimer'] },
    { position: 'Vice President', names: ['Evan Axelbaum'] },
    { position: "President, Women's Division", names: ['Sabrina Gornish'] },
    { position: 'Secretary', names: ['Robyn Haspel'] },
    { position: 'Treasurer', names: ['Nachum Matten'] },
    { position: 'Membership/Hospitality Committee Chair', names: ['Ruthie Shach'] },
    { position: 'Security, Building, and Infrastructure Committee Chair', names: ['Seth Near'] },
    { position: 'General/Adult Education Committee Chair', names: ['Michael Schachter'] },
    {
      position: 'Trustee',
      names: ['Mark Friedman', 'Roz Hirschhorn', 'Jill Mogil']
    },
    {
      position: 'Gabbai',
      names: ['Danny Wachsstock', 'Daniel Siegel', 'Elliott Franklin', 'Yosef Zeff']
    }
  ];

  const historyTimeline = [
    {
      heading: '1940s: A grassroots beginning',
      details:
        'Young Israel of St. Louis launched with a dedicated group of young families who created minyanim, youth groups, and social programming even as members left to serve in World War II.'
    },
    {
      heading: '1950s–1970s: Building a home',
      details:
        'The congregation moved from borrowed spaces into its own Groby Road building, welcomed Rabbi Simcha Krauss, and became known for spirited tefillah, thoughtful drashot, and a culture of learning.'
    },
    {
      heading: '1980s–1990s: Expanding impact',
      details:
        "Under Rabbi Jeffrey Bienenfeld the shul broadened its learning opportunities, inspired ba'alei teshuva families, and in 1994 celebrated the opening of its current Delmar Boulevard campus."
    },
    {
      heading: '2007–2025: Thriving community life',
      details:
        'Rabbi Moshe and Baila Shulman stewarded years of remarkable growth, strengthening minyanim, shiurim, youth programs, and the welcoming spirit that defines our kehillah.'
    },
    {
      heading: '2025–Today: A renewed chapter',
      details:
        'Rabbi Dov and Sara Fink bring warm leadership, collaborative energy, and a commitment to Torah, tefillah, and chesed as the congregation embraces its next era.'
    }
  ];

  useEffect(() => {
    setActiveTabIndex(!currentTab ? 0 : tabs.findIndex((el) => el === currentTab));
  }, []);

  const handleTabChange = (newTabIndex) => {
    setActiveTabIndex(newTabIndex);
    if (newTabIndex === 0) {
      window.history.replaceState(null, null, '/about');
    } else {
      window.history.replaceState(null, null, `/about/${tabs[newTabIndex]}`);
    }
  };

  return (
    <>
      <div className='page-header text-center d-flex container-fluid justify-content-center align-items-center'>
        <h1>About YI</h1>
      </div>
      <TabView
        className='mt-1 nav-ribbon'
        activeIndex={activeTabIndex}
        onTabChange={(e) => handleTabChange(e.index)}
        scrollable
      >
        <TabPanel header='About YI'>
          <Container className='py-4'>
            <Row className='align-items-center gy-4'>
              <Col lg={5}>
                <img
                  src={aboutBanner}
                  alt='Young Israel of St. Louis sanctuary'
                  className='img-fluid rounded shadow-sm'
                />
              </Col>
              <Col lg={7}>
                <p>
                  Young Israel of St. Louis is a Modern Orthodox synagogue affiliated with the National Council of Young Israel. We
                  are home to more than 160 families who come together for tefillah, Torah learning, and meaningful relationships.
                </p>
                <p>
                  The shul is powered by active lay leadership, a vibrant Women&apos;s Division, and a dedicated chesed team that supports
                  members and neighbors in moments of need.
                </p>
                <ul className='ps-3'>
                  <li>Daily and Shabbat minyanim plus youth, teen, and tot programming</li>
                  <li>Classes and guest lecturers for men, women, and students at every level</li>
                  <li>Community initiatives supporting Israel and local communal partners</li>
                </ul>
              </Col>
            </Row>
          </Container>
        </TabPanel>
        <TabPanel header='History'>
          <Container className='py-4'>
            <Row className='align-items-start gy-4'>
              <Col lg={5}>
                <img
                  src={oldBuilding}
                  alt='Historic photo of the Young Israel building'
                  className='img-fluid rounded shadow-sm'
                />
              </Col>
              <Col lg={7}>
                <h2 className='text-center text-lg-start fw-bold mb-4'>Our Story at a Glance</h2>
                <p>
                  For more than seven decades, Young Israel of St. Louis has anchored Orthodox Jewish life in the region. Here are a few
                  highlights from our journey:
                </p>
                <ul className='timeline ps-3'>
                  {historyTimeline.map((item, index) => (
                    <li key={index} className='mb-3'>
                      <strong>{item.heading}</strong>
                      <br />
                      {item.details}
                    </li>
                  ))}
                </ul>
                <p>
                  Today our campus on Delmar Boulevard is filled with multigenerational families, engaging shiurim, and spirited
                  Shabbatot. We remain deeply connected to the broader St. Louis community, its day schools, youth movements, and
                  Israel-focused organizations.
                </p>
              </Col>
            </Row>
          </Container>
        </TabPanel>

        <TabPanel header='Meet The Rabbi'>
          <Container className='py-4'>
            <Row className='gy-4 align-items-center'>
              <Col lg={5} className='d-flex justify-content-center'>
                <div className='about-rabbi-portrait-frame'>
                  <img src={rabbiFinkPortrait} alt='Rabbi Dov Fink' />
                </div>
              </Col>
              <Col lg={7}>
                <h2 className='fw-bold mb-3 text-center text-lg-start'>Rabbi Dov Fink</h2>
                <p>
                  Rabbi Fink joined Young Israel of St. Louis in 2025, bringing more than a decade of community leadership, pastoral
                  care, and Torah teaching experience to the role of mara d&apos;atra. He received semicha after years of intensive study
                  in Israel and New York and remains passionate about translating halacha and hashkafa into meaningful daily living.
                </p>
                <p>
                  He has guided shuls, day schools, and campus initiatives throughout North America, building inclusive programs that
                  empower teens, young professionals, and multi-generational families to deepen their connection to tefillah and
                  learning. Rabbi Fink is known for shiurim that weave together Tanach, halacha, and contemporary challenges with
                  warmth and approachability.
                </p>
                <p>
                  Together with his wife and children, Rabbi Fink looks forward to welcoming you into a home that pulses with chesed,
                  hospitality, and spirited song. The Fink family is eager to get to know longtime members and newcomers alike and to
                  help every household find its place in our kehillah.
                </p>
                <ul className='ps-3'>
                  <li>Open-door Torah learning, chavruta opportunities, and thoughtful drashot each week</li>
                  <li>Pastoral support and lifecycle guidance rooted in empathy and halachic sensitivity</li>
                  <li>Collaborative leadership that partners with lay volunteers across committees and initiatives</li>
                </ul>
                <p className='mb-0'>
                  <strong>Email:</strong>{' '}
                  <a href='mailto:rabbi@youngisrael-stl.org'>rabbi@youngisrael-stl.org</a>
                </p>
              </Col>
            </Row>
            <Row className='mt-4 justify-content-center'>
              <Col lg={10}>
                <div className='about-rabbi-family-frame'>
                  <img src={rabbiFinkFamily} alt='Rabbi Dov Fink and family' />
                </div>
              </Col>
            </Row>
          </Container>
        </TabPanel>
        <TabPanel header='Membership'>
          <Container className='py-4'>
            <Row className='justify-content-center text-center'>
              <Col lg={8}>
                <h2 className='fw-bold mb-3'>Interested in Becoming a Member?</h2>
                <p>
                  We would love to welcome you. New members receive a reduced first-year dues rate and a personal meeting with Rabbi
                  Fink to help you get connected to minyanim, learning, and volunteer opportunities.
                </p>
                <p className='mb-0'>
                  Call the shul office at <a href='tel:13147271880'>314-727-1880</a> or email{' '}
                  <a href='mailto:info@youngisrael-stl.org'>info@youngisrael-stl.org</a> to begin the conversation.
                </p>
              </Col>
            </Row>
          </Container>
        </TabPanel>
        <TabPanel header='Meet The Board'>
          <Container className='py-4'>
            <DataTable
              className='mx-auto about-board-table'
              value={boardColumns}
              style={{ maxWidth: '860px' }}
              stripedRows
              showHeaders={false}
              breakpoint='0px'
            >
              <Column field='position' bodyClassName='fw-bold' />
              <Column
                body={(rowData) => {
                  return (
                    <ul style={{ listStyle: 'none', paddingLeft: 0, marginBottom: 0 }}>
                      {rowData.names.map((n, i) => {
                        return (
                          <li key={i} style={{ whiteSpace: 'nowrap' }}>
                            {n}
                          </li>
                        );
                      })}
                    </ul>
                  );
                }}
                bodyClassName='align-items-center'
              />
            </DataTable>
          </Container>
        </TabPanel>
        <TabPanel header='Contact Us'>
          <Container className='py-4'>
            <Row className='gy-4'>
              <Col lg={5}>
                <p className='fw-bold mb-1'>We are conveniently located at:</p>
                <p className='mb-3'>
                  <span>8101 Delmar Blvd.</span>
                  <br />
                  <span>St. Louis, MO 63130</span>
                </p>
                <p className='mb-1'>
                  <strong>Email:</strong>{' '}
                  <a href='mailto:info@youngisrael-stl.org'>info@youngisrael-stl.org</a>
                </p>
                <p className='mb-1'>
                  <strong>Rabbi's Email:</strong>{' '}
                  <a href='mailto:rabbi@youngisrael-stl.org'>rabbi@youngisrael-stl.org</a>
                </p>
                <p>
                  <strong>Phone:</strong>{' '}
                  <a href='tel:13147271880'>314-727-1880</a>
                </p>
              </Col>
              <Col lg={7}>
                <Ratio aspectRatio='16x9'>
                  <iframe
                    title='Young Israel of St. Louis on Google Maps'
                    src='https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3115.494278382756!2d-90.3491829236376!3d38.66050716064618!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87df34cfd581d1d3%3A0xc9493ca6d726ca6c!2sYoung%20Israel%20of%20St.%20Louis!5e0!3m2!1sen!2sus!4v1723065810532!5m2!1sen!2sus'
                    style={{ border: 0 }}
                    allowFullScreen
                    loading='lazy'
                    referrerPolicy='no-referrer-when-downgrade'
                  ></iframe>
                </Ratio>
              </Col>
            </Row>
          </Container>
        </TabPanel>
      </TabView>
    </>
  );
};

export default About;
